import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { GitCommitHorizontal, Clock, TrendingUp, AlertCircle, Users, ChevronRight, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dashboard analytique des divergences Statut × Facturation (Phase 2 Observe & Learn).
 * - Répartition par type (ahead_financial, ahead_operational, inconsistent)
 * - Top 5 opérations anciennes (pour priorisation audit)
 * - Derniers événements loggés (trace des mutations récentes)
 */
export const WorkflowDivergencesDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/dashboard/workflow-divergences`, {});
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="p-6 text-center text-gray-500">Chargement...</div>;
  if (error) return <div className="p-6 text-center text-red-600">{error}</div>;
  if (!data) return null;

  const counts = data.counts || {};
  const total = counts.total || 0;
  const active = data.active || [];
  const logs = data.recentLogs || [];

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return d; }
  };

  const typeLabels = {
    ahead_financial: { label: 'Facturation avancée', color: 'bg-amber-100 text-amber-800', desc: 'Facture émise avant livraison' },
    ahead_operational: { label: 'Livraison avancée', color: 'bg-blue-100 text-blue-800', desc: 'Livrée/terminée sans paiement à jour' },
    inconsistent: { label: 'Contradictoire', color: 'bg-red-100 text-red-800', desc: 'Ex: annulée + payée' },
    unknown: { label: 'Autre', color: 'bg-gray-100 text-gray-800', desc: 'Pattern non classifié' },
  };

  return (
    <div className="space-y-6" data-testid="workflow-divergences-dashboard">
      {/* Header + KPI */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <GitCommitHorizontal className="w-6 h-6 text-blue-600" />
            Cohérence Statut × Facturation
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Observe & Learn — analyse des divergences entre cycle opérationnel et cycle financier (opérations SPOT, non-legacy).
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/operations?divergence=1')}
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700"
          data-testid="view-divergent-operations"
        >
          Voir toutes les opérations <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{total}</p>
            <p className="text-xs text-gray-400 mt-1">opérations en divergence</p>
          </CardContent>
        </Card>
        {Object.entries(typeLabels).map(([key, meta]) => (
          <Card key={key}>
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{meta.label}</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{counts[key] || 0}</p>
              <p className="text-xs text-gray-400 mt-1">{meta.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Top anciennes */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="w-4 h-4" />
            Top 10 opérations en divergence (triées par ancienneté)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {active.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">Aucune divergence détectée. Parfait !</div>
          ) : (
            <div className="divide-y">
              {active.slice(0, 10).map((op) => {
                const meta = typeLabels[op.divergenceType] || typeLabels.unknown;
                return (
                  <div
                    key={op.id}
                    className="flex items-center justify-between gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/admin/operations/${op.id}`)}
                    data-testid={`divergent-op-${op.operationNumber}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${meta.color}`}>
                        {meta.label}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{op.operationNumber}</p>
                        <p className="text-xs text-gray-500 truncate">{op.companyName}</p>
                      </div>
                    </div>
                    <div className="text-right text-xs space-y-0.5 whitespace-nowrap">
                      <p className="font-mono text-gray-700">{(op.estimatedPrice || 0).toLocaleString('fr-FR')} FCFA</p>
                      <p className="text-gray-400">{op.daysOpen} jours ouverte</p>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Logs récents */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="w-4 h-4" />
            Dernières mutations (20 plus récentes)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">
              Aucun log enregistré pour l'instant. Les divergences créées depuis le 20/04/2026 apparaîtront ici.
            </div>
          ) : (
            <div className="divide-y text-sm">
              {logs.map((log) => (
                <div key={log.id} className="p-3 hover:bg-gray-50" data-testid={`log-${log.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{log.operationNumber || log.operationId}</span>
                    <span className="text-xs text-gray-400">{formatDate(log.createdAt)}</span>
                  </div>
                  <div className="text-xs text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
                    <span className="bg-gray-100 px-1.5 py-0.5 rounded">{log.source}</span>
                    <span className="text-gray-400">•</span>
                    <span>{log.before?.status}/{log.before?.financialStatus || '—'}</span>
                    <span>→</span>
                    <span className="font-medium">{log.after?.status}/{log.after?.financialStatus || '—'}</span>
                    {log.userEmail && (
                      <>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-500 flex items-center gap-1"><Users className="w-3 h-3" />{log.userEmail}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Aide interprétation */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3 text-sm">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-blue-900">
          <p className="font-semibold mb-1">Interprétation</p>
          <ul className="list-disc list-inside space-y-0.5 text-xs text-blue-800">
            <li><strong>Livraison avancée</strong> : opération livrée/terminée mais statut financier pas à jour → souvent un paiement hors plateforme oublié.</li>
            <li><strong>Facturation avancée</strong> : facture émise alors que l'opération n'est pas encore livrée → erreur de qualification ou facture d'acompte.</li>
            <li><strong>Contradictoire</strong> : incohérence logique (ex: annulée + payée) → nécessite une correction manuelle immédiate.</li>
            <li>Les contrats et imports legacy sont <strong>exclus</strong> (divergence par design).</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default WorkflowDivergencesDashboard;
